import { FindParams, getCountryNameByCode } from '../../../../utils'
import { BaseComponent } from '@core/classes/base-component'
import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { LoaderService } from '@core/services/loader.service'
import { ModalService } from '@core/services/modal.service'
import { NotificationService } from '@core/services/notification.service'
import { OrderForm } from '../../forms/order/order.form'
import {
	ContractorRead,
	ContractorReadList,
	ContractorsService,
	ConveyanceTypeRead,
	CurrentUserService,
	EntityRead,
	EntityReadList,
	EntityReadShort,
	OrderRead,
	OrderReadList,
	OrderReadListItem,
	OrdersService,
	StatusRead,
	UserRead,
	UserReadList,
	UsersService,
	ValueDictionaryRead,
} from '@app/generated/'
import { TableQuery } from '@core/types/table-query'
import { finalize, forkJoin, lastValueFrom, Subscription } from 'rxjs'
import { CardComponent } from '@core/components/card/card.component'
import { IconButtonComponent } from '@core/components/icon-button/icon-button.component'
import { ButtonComponent } from '@core/components/button/button.component'
import { TooltipDirective } from '@core/directives/tooltip.directive'
import { IconComponent } from '@core/components/icon/icon.component'
import { TableComponent } from '@core/components/table/table.component'
import { TableHeaderComponent } from '@core/components/table-header/table-header.component'
import { FormFieldComponent } from '@core/components/form-field/form-field.component'
import { InputComponent } from '@core/components/input/input.component'
import { SelectComponent } from '@core/components/select/select.component'
import { OptionComponent } from '@core/components/option/option.component'
import { AutocompleteComponent } from '@core/components/autocomplete/autocomplete.component'
import { TableRowDataDirective } from '@core/directives/table-row-data.directive'
import { Router } from '@angular/router'
import { CommonModule } from '@angular/common'
import { TagComponent } from '@core/components/tag/tag.component'
import { ConfirmationComponent } from '@core/components/confirmation/confirmation.component'
import { isArray } from '@core/functions/conditions'
import { LanguageService } from '@core/services/language.service'
import { CountryCode } from '@core/enums/country-code.enum'
import { getLanguageCode } from '@app/general/utils/get-language-code'
import { CsvExportService } from './../../services/csv-export.service'
import { ChangeStatusComponent } from '@app/orders/forms/change-status/change-status.form'
import { OrderKanbanComponent } from '../order-kanban/order-kanban.component'
import { getItemTranslation } from '@app/general/utils/get-item-translation'
import { Pagination } from '@core/classes/pagination'
import { PaginationComponent } from '@core/components/pagination/pagination.component'
import { SocketOrderResponse, SocketsService } from '@app/general/services/sockets.service'
import { Logger } from '@app/general/utils/logger.util'
import { dateRangeValidator } from '@app/general/utils/forms'
import { FormFieldGroupComponent } from '@core/components/form-field-group/form-field-group.component'
import { ChangeConveyanceTypeComponent } from '@app/orders/forms/change-conveyance-type/change-conveyance-type.form'
import { AclService } from '@app/acl/services/acl.service'
import { Permission } from '@app/acl/enums/permissions.enum'
import { OrderStatusUpdateComponent } from '@app/orders/components/order-status-update/order-status-update.component'
import { config } from '@config'
import { getTextColor } from '@app/general/utils/color-manipulation'
import { ChangeAssignationComponent } from '@app/orders/forms/change-assignation/change-assignation.component'
import { AssignedType } from '@app/orders/forms/preview-task/preview-task.form'
import { OrderDeliveryComponent } from '@app/orders/components/order-delivery/order-delivery.component'
import { EntityChangeComponent } from '@app/orders/components/entity-change/entity-change.component'
import { ModalComponent } from '@core/components/modal/modal.component'
import { PickupTerminalRead } from '@app/generated/model/pickupTerminalRead'
import { OrderQueryParamKeysEnum } from '@app/orders/functions/orders.utils'
import { LayoutService } from '@core/services/layout.service'

export type OrdersViewType = 'list' | 'kanban'

export const OrderViewLocalStorageKey = 'orders-view-type'
export const OrderFiltersLocalStorageKey = 'orders-view-list-filters'
export const OrderFiltersStyleLocalStorageKey = 'orders-view-list-filters-style'
export const OrderListScrollStorageKey = 'orders-view-list-scroll'

export enum OrderFiltersStyleEnum {
	default = 'default',
	column = 'column',
}

export interface KanbanCard {
	order: OrderReadListItem
}

export interface KanbanColumn {
	id: string
	name: string
	color: string
	data: KanbanCard[]
}

interface StatusItem {
	status?: StatusRead
	dividerText?: string
}

export interface ScrollItem {
	scrollY: number
	scrollTargetId: string
}

export interface OrdersFiltersInterface {
	search: FormControl<string | null>
	takeDateFrom: FormControl<string | null>
	takeDateTo: FormControl<string | null>
	conveyanceType: FormControl<ConveyanceTypeRead[] | null>
	takenBy: FormControl<UserRead[] | null>
	entity: FormControl<EntityRead[] | null>
	status: FormControl<StatusRead[] | null>
	contractor: FormControl<ContractorRead[] | null>
	delivery_confirmation: FormControl<AssignedType | null>
	orderNumber: FormControl<string | null>
	enteringPortDate: FormControl<string | null>
	comments: FormControl<string | null>
	terminal: FormControl<PickupTerminalRead | null>
	blNumber: FormControl<string | null>
	containerNumber: FormControl<string | null>
	enteringPortDateFrom: FormControl<string | null>
	enteringPortDateTo: FormControl<string | null>
	without_customs_task: FormControl<string | null>
}

export type OrdersFiltersValues = {
	[P in keyof OrdersFiltersInterface]: OrdersFiltersInterface[P]['value']
}
export type OrdersFiltersAndSearchValues = {
	filters: OrdersFiltersValues
	sortBy?: string
	sortOrder?: 'asc' | 'desc'
	perPage?: number
	pageNumber?: number
}

@Component({
	standalone: true,
	selector: 'order-list',
	templateUrl: './order-list.component.html',
	styleUrl: './order-list.component.scss',
	imports: [
		CardComponent,
		IconButtonComponent,
		ButtonComponent,
		TooltipDirective,
		IconComponent,
		TableComponent,
		TableHeaderComponent,
		FormFieldComponent,
		InputComponent,
		SelectComponent,
		OptionComponent,
		AutocompleteComponent,
		TableRowDataDirective,
		CommonModule,
		TagComponent,
		OrderKanbanComponent,
		PaginationComponent,
		FormFieldGroupComponent,
	],
})
export class OrderList extends BaseComponent {
	@Input() contractorId: string | null = null
	@Input() entityId: string | null = null
	@Output() orderDeletionSuccess = new EventEmitter<void>()
	@Output() statusChange: EventEmitter<boolean | undefined> = new EventEmitter()

	refreshFilters = false
	statuses: StatusRead[] = []
	statusesFilters: StatusItem[] = []
	conveyanceTypes: ConveyanceTypeRead[] = []
	model: OrderReadList = {
		total: 0,
		items: [],
	}
	params = new FindParams()
	pagination = new Pagination()
	filters!: FormGroup<OrdersFiltersInterface>
	selectedCheckboxes: Array<any> = []
	users: UserReadList = { total: 0, items: [] }
	contractors: ContractorReadList = { total: 0, items: [] }
	entities: EntityReadList = { total: 0, items: [] }
	showKanban: boolean = false
	columns: KanbanColumn[] = []
	pickupTerminals: PickupTerminalRead[] = []
	isLoaded: boolean = false
	scrollY: number = 0

	isColumnFilters = false

	@ViewChild(OrderKanbanComponent) orderKanbanComponent: OrderKanbanComponent = {} as OrderKanbanComponent

	@ViewChild('tableComponent') tableComponent?: TableComponent

	protected readonly getItemTranslation = getItemTranslation
	protected readonly textAddNewOrder = $localize`Add new order`
	protected readonly textEnterPhrase = $localize`Enter phrase`
	protected readonly textOrderDate = $localize`Order date`
	protected readonly textDateFrom = $localize`From`
	protected readonly textDateTo = $localize`To`
	protected readonly textTransport = $localize`Conveyance type`
	protected readonly textSearchTransport = $localize`Search`
	protected readonly textOrderRecipient = $localize`Order assigned to`
	protected readonly textSearchRecipient = $localize`Search`
	protected readonly textContractor = $localize`Contractor`
	protected readonly textEntity = $localize`Contractor`
	protected readonly textSearchContractor = $localize`Search`
	protected readonly textStatus = $localize`Status`
	protected readonly textSearchStatus = $localize`Select`
	protected readonly textFilter = $localize`Filter Orders`
	protected readonly textRemoveFilter = $localize`Clear filters`
	protected readonly textDeleteSelectedOrdersButton = $localize`Delete selected orders`
	protected readonly textPaid = $localize`Paid`
	protected readonly textCsvColumnContainerNumber = $localize`Container number`
	protected readonly textCsvColumnOrderNumber = $localize`Order number`
	protected readonly textCsvColumnConveyanceType = $localize`Conveyance type`
	protected readonly textCsvColumnTakeDate = $localize`Take date`
	protected readonly textCsvColumnDeliveryAddress = $localize`Delivery address`
	protected readonly textCsvColumnStatus = $localize`Status`
	protected readonly textCsvColumnTakenBy = $localize`Taken by`
	protected readonly textCsvFilename = $localize`orders`
	protected readonly textCsvColumnCsvPin = $localize`Pin`
	protected readonly textCsvColumnBlNumber = $localize`BL number`
	protected readonly textCsvColumnWeight = $localize`Weight`
	protected readonly textCsvColumnSize = $localize`Size`
	protected readonly textCsvColumnATB = $localize`ATB`
	protected readonly textCsvColumnATC = $localize`ATC`
	protected readonly textCsvColumnMRN = $localize`MRN`
	protected readonly textCsvColumnEnteringPortDate = $localize`Entering port date`
	protected readonly textCsvColumnTerminal = $localize`Terminal`
	protected readonly textCsvColumnShip = $localize`Ship`
	protected readonly textCsvColumnShipowner = $localize`Shipowner`
	protected readonly textCsvColumnEmptyReturnAddress = $localize`Empty return address`
	protected readonly textCsvColumnLoadingPort = $localize`Loading port`
	protected readonly textCsvColumnLogisticStatus = $localize`Logistic status`
	protected readonly textCsvColumnFinancialStatus = $localize`Financial status`
	protected readonly textCsvColumnCustomsStatus = $localize`Customs status`
	protected readonly textShowKanban = $localize`Show kanban board`

	protected readonly textOrderPreview = $localize`Order preview`
	protected readonly textCreateOrderTemplate = $localize`Create new order using values from this order`
	protected readonly textChangeStatus = $localize`Change status`
	protected readonly textOpenNewWindow = $localize`Open in a new window`
	protected readonly textDelete = $localize`Delete`
	protected readonly textConveyanceType = $localize`Conveyance type`
	protected readonly textOrderPaymentStatus = $localize`Order payment status`
	protected readonly textContainerLogisticStatus = $localize`Container logistic status`
	protected readonly textContainerCustomsStatus = $localize`Container customs status`
	protected readonly textChangeAssignation = $localize`Assign user to order`
	protected readonly textChangeAssignationOrders = $localize`Assign user to selected orders`
	protected readonly textChangeStatusOrders = $localize`Change status of orders`
	protected readonly textHasDeliveryConfirmation = $localize`Delivery confirmation`
	protected readonly textBlockade = $localize`Release blockade`
	protected readonly textSearch = $localize`Search`
	protected readonly textFiltersStyle = $localize`Filters style`
	protected readonly textEnterPortDateFrom = $localize`Enter from`
	protected readonly textEnterPortDateTo = $localize`Enter to`
	protected readonly textEnterPortDate = $localize`Entering port date`
	protected readonly textComments = $localize`Comments`
	protected readonly textTerminal = $localize`Terminal`
	protected readonly textCopy = $localize`Copy to clipboard`
	protected readonly textWithoutCustomsTask = $localize`Without customs task`

	protected readonly textCsvColumnCreatedAt = $localize`Created At`
	protected readonly textCsvColumnUpdatedAt = $localize`Updated At`
	protected readonly textCsvColumnOrderPaymentStatus = $localize`Order Payment Status`
	protected readonly textCsvColumnContainerLogisticStatus = $localize`Container Logistic Status`
	protected readonly textCsvColumnContainerClearanceStatus = $localize`Container Clearance Status`
	protected readonly textCsvColumnPriority = $localize`Priority`
	protected readonly textCsvColumnEntity = $localize`Entity`
	protected readonly textCsvColumnPickupTerminal = $localize`Pickup Terminal`
	protected readonly textCsvColumnDSK = $localize`DSK`
	protected readonly textCsvColumnShipName = $localize`Ship Name`
	protected readonly textCsvColumnTakenByEmail = $localize`Taken By Email`
	protected readonly textCsvColumnAssignedTo = $localize`Assigned To`
	protected readonly textCsvColumnAssignedToEmail = $localize`Assigned To Email`
	protected readonly textCsvColumnHasDeliveryConfirmation = $localize`Has Delivery Confirmation`
	protected readonly textCsvColumnComment = $localize`Comment`
	protected readonly textCsvColumnPackageCount = $localize`Package Count`
	protected readonly textChangeOfContractor = $localize`Change of contractor`
	protected readonly textContractorGroup = $localize`Contractor group`

	protected readonly getTextColor = getTextColor

	private readonly logger: Logger = new Logger(OrderList.name, {
		infoBackground: '#fdd0ff',
		infoColor: '#1e1e1e',
	})
	private tableFiltersTimeout: any
	private filtersValue = this.filters?.value
	private subscriptions: Subscription[] = []

	constructor(
		private ordersService: OrdersService,
		private modalService: ModalService,
		private notificationService: NotificationService,
		private loaderService: LoaderService,
		private changeDetectorRef: ChangeDetectorRef,
		private usersService: UsersService,
		private contractorService: ContractorsService,
		private languageService: LanguageService,
		private csvExportService: CsvExportService,
		private router: Router,
		private socketsService: SocketsService,
		private aclService: AclService,
		private currentUserService: CurrentUserService,
		private layoutService: LayoutService,
	) {
		super()
		this.subscriptions.push(
			this.socketsService.orderUpdated$().subscribe((order: SocketOrderResponse) => {
				if (order.order_id) {
					if (!this.showKanban) {
						this.logger.info(`Received updated order: ${JSON.stringify(order.order_id)}`)
						const index = this.model.items.findIndex(elem => elem.id == order.order_id)
						if (index >= 0) {
							this.ordersService.getOrder(order.order_id).subscribe({
								next: (response: OrderRead) => {
									this.model.items[index] = response
								},
							})
						}
					}
				}
			}),
		)
		this.fetchTerminal()
		this.isColumnFilters = sessionStorage.getItem(OrderFiltersStyleLocalStorageKey) == OrderFiltersStyleEnum.column
	}

	get getLocalStorageViewType(): OrdersViewType {
		return (sessionStorage.getItem(OrderViewLocalStorageKey) as OrdersViewType) ?? 'list'
	}

	get getLocalStorageFilterAndSort(): OrdersFiltersAndSearchValues | undefined {
		if (!this.isOrdersPage) {
			return
		}
		const storedFilters = sessionStorage.getItem(OrderFiltersLocalStorageKey)

		if (!storedFilters) {
			return undefined
		}

		return JSON.parse(storedFilters)
	}

	get getLocalStorageScroll(): ScrollItem | undefined {
		if (!this.isOrdersPage) {
			return
		}
		const storedFilters = sessionStorage.getItem(OrderListScrollStorageKey)

		if (!storedFilters) {
			return undefined
		}

		return JSON.parse(storedFilters)
	}

	get isOrdersPage(): boolean {
		return this.router.url.includes(`/${config.modules.orders.path}`)
	}

	openAddOrderModal() {
		this.loaderService.setContain().enable(async () => {
			const modal = await this.modalService.open(OrderForm)
			modal.title = $localize`Add order`
			modal.iconName = 'add-order'
			modal.width = '1200px'
			modal.showConfirmationOnClickOutside = true
			const instance = modal.componentInstance
			instance.isNotModal = false
			if (instance) {
				instance.orderCreated.subscribe(() => {
					this.loaderService.setContain().enable(this.fetchData.bind(this))
				})
			}
		})
	}

	async ngOnInit(): Promise<void> {
		this.initFilters()
		const filters = this.getLocalStorageFilterAndSort
		this.filtersValue = JSON.parse(JSON.stringify(this.filters.value))
		const scroll = this.getLocalStorageScroll

		if (scroll?.scrollY) {
			const interval = setInterval(() => {
				if (this.isLoaded) {
					this.layoutService.layoutContainer?.nativeElement.scrollTo({
						top: scroll.scrollY ?? 0,
					})
					clearInterval(interval)
				}
			}, 300)
		}

		if (filters) {
			this.filters.controls.search.setValue('')
			this.filters.patchValue(filters.filters)
			this.params.sortBy = filters.sortBy
			this.params.sortOrder = filters.sortOrder
			this.pagination.setPerPage(filters.perPage ?? 25)
		}
		if (this.getLocalStorageViewType === 'kanban') {
			this.fetchStatuses(true)
		} else {
			this.fetchStatuses()
		}
		this.fetchConveyanceTypes()
		if (this.aclCanReadContractors()) {
			this.fetchContractors()
		}
		if (this.aclCanReadEntities()) {
			this.fetchEntities()
		}
		if (this.aclCanReadUsers()) {
			this.fetchUsers()
		}

		this.pagination.listShouldBeReloaded().subscribe(() => {
			this.loaderService.setContain().enable(this.fetchData.bind(this))
		})

		this.layoutService.afterScroll.subscribe(event => {
			const target = event!.target as HTMLElement

			if (target) {
				this.scrollY = target.scrollTop
			}
		})

		this.pagination.currentPageChanges().subscribe(() => {
			if (this.isLoaded) {
				sessionStorage.removeItem(OrderListScrollStorageKey)
			}
		})
	}

	setTableViewSortValue(): void {
		setTimeout(() => {
			if (!!this.tableComponent && !!this.params.sortOrder && !!this.params.sortBy) {
				this.tableComponent.setTableSortValue(this.params.sortBy, this.params.sortOrder)
			}
		}, 0)
	}

	queryChanged(query: TableQuery) {
		if (query.sort) {
			this.params.sortBy = query.sort.sortKey
			this.params.sortOrder = query.sort.sortDir
		} else {
			this.params.sortBy = undefined
			this.params.sortOrder = undefined
		}
		// this.setSessionStorageSortValues()
		this.loaderService.setContain().enable(this.fetchData.bind(this))
	}

	checkboxesChanged(checkboxes: OrderRead[]) {
		this.selectedCheckboxes = checkboxes
	}

	formatDateStringFrom(value: string): string {
		const date = new Date(value)
		date.setUTCDate(date.getUTCDate() + 1)
		date.setUTCHours(0, 0, 0, 0)
		return date.toISOString()
	}

	formatDateStringTo(value: string, onlyDate: boolean = false): string {
		const date = new Date(value)
		date.setUTCDate(date.getUTCDate() + 1)
		if (onlyDate) {
			date.setUTCHours(0, 0, 0, 0)
		} else {
			date.setUTCHours(23, 59, 59, 99)
		}

		return date.toISOString()
	}

	async fetchData(silent: boolean = false) {
		if (!silent) {
			this.loaderService.setContain().enable()
		}
		this.setLocalStorageFiltersAndSort()
		this.filtersValue = JSON.parse(JSON.stringify(this.filters.value))
		let search = this.filters.controls.search.value ?? undefined
		if (search) {
			if (search.match(/[A-Z]{4}[-\s]\d*/)) {
				search = search.replace(/[\s-]/g, '')
			}
		}

		if (!this.aclService.hasPermission(Permission.ORDERS_READ)) {
			this.currentUserService
				.getOrders(this.pagination.offset(), this.pagination.perPage)
				.pipe(
					finalize(() => {
						if (!silent) {
							this.loaderService.disable()
						}
					}),
				)
				.subscribe({
					next: response => {
						this.model = response
						this.pagination.setTotal(response.total)
						if (!this.isLoaded) {
							const filters = this.getLocalStorageFilterAndSort
							if (filters) {
								this.pagination.setCurrentPage(filters.pageNumber ?? 1)
							}
							this.isLoaded = true
						}
						this.setLocalStoragePageNumber()
					},
					error: () => {
						this.notificationService.error($localize`Failed to fetch order list`)
					},
				})

			return
		}

		const data$ = this.ordersService
			.getOrders(
				this.pagination.offset(),
				this.pagination.perPage,
				search,
				false,
				this.params.sortOrder ?? undefined,
				(this.params.sortBy as any) ?? undefined,
				this.filters.controls.takeDateFrom.value
					? this.formatDateStringFrom(this.filters.controls.takeDateFrom.value)
					: undefined,
				this.filters.controls.takeDateTo.value
					? this.formatDateStringTo(this.filters.controls.takeDateTo.value)
					: undefined,
				this.filters.controls.conveyanceType.value?.map(item => item.id) ?? undefined,
				undefined,
				this.entityId ? [this.entityId] : this.filters.controls.entity.value?.map(item => item.id) ?? undefined,
				this.filters.controls.status.value
					?.map(item => (item.category === 'order' ? item.id : undefined))
					.filter((id): id is string => id !== undefined) ?? undefined,
				this.contractorId
					? [this.contractorId]
					: this.filters.controls.contractor.value?.map(item => item.id) ?? undefined,
				undefined,
				undefined,
				undefined,
				this.filters.controls.status.value
					?.map(item => (item.category === 'container-logistic' ? item.id : undefined))
					.filter((id): id is string => id !== undefined) ?? undefined,
				this.filters.controls.status.value
					?.map(item => (item.category === 'container-clearance' ? item.id : undefined))
					.filter((id): id is string => id !== undefined) ?? undefined,
				this.filters.controls.status.value
					?.map(item => (item.category === 'order-payment' ? item.id : undefined))
					.filter((id): id is string => id !== undefined) ?? undefined,
				this.filters.controls.takenBy.value?.map(item => item.id) ?? undefined,
				this.filters.controls.delivery_confirmation.value
					? this.filters.controls.delivery_confirmation.value === 'selected'
					: undefined,
				undefined,
				this.filters.value.orderNumber || undefined,
				this.filters.value.containerNumber || undefined,
				this.filters.value.blNumber || undefined,
				this.filters.value.enteringPortDate
					? this.formatDateStringFrom(this.filters.value.enteringPortDate)
					: undefined,
				this.filters.value.terminal?.id || undefined,
				this.filters.value.enteringPortDateFrom
					? this.formatDateStringFrom(this.filters.value.enteringPortDateFrom)
					: undefined,
				this.filters.value.enteringPortDateTo
					? this.formatDateStringTo(this.filters.value.enteringPortDateTo, true)
					: undefined,
				this.filters.value.without_customs_task
					? this.filters.controls.without_customs_task.value === 'selected'
					: undefined,
			)
			.pipe(
				finalize(() => {
					if (!silent) {
						this.loaderService.disable()
					}
				}),
			)

		try {
			const response: OrderReadList = await lastValueFrom(data$)
			this.pagination.setTotal(response.total)
			if (!this.isLoaded) {
				const filters = this.getLocalStorageFilterAndSort
				if (filters) {
					this.pagination.setCurrentPage(filters.pageNumber ?? 1)
				}
				this.isLoaded = true
			}
			this.setLocalStoragePageNumber()
			this.model = response
			this.changeDetectorRef.detectChanges()
		} catch (error) {
			const message = $localize`Was not able to fetch data`
			this.notificationService.error(message)
		}
	}

	exportOrdersToCSV() {
		if (!this.selectedCheckboxes.length) {
			this.notificationService.error($localize`There are no selected orders to export`)
			return
		}

		if (this.aclService.hasPermission(Permission.ADMIN)) {
			this.generateAdminCSV()
			return
		}

		const headersMap = {
			container_number: this.textCsvColumnContainerNumber,
			number: this.textCsvColumnOrderNumber,
			conveyance_type: this.textCsvColumnConveyanceType,
			take_date: this.textCsvColumnTakeDate,
			delivery_address: this.textCsvColumnDeliveryAddress,
			status: this.textCsvColumnStatus,
			taken_by: this.textCsvColumnTakenBy,
			pin: this.textCsvColumnCsvPin,
			bl_number: this.textCsvColumnBlNumber,
			weight: this.textCsvColumnWeight,
			size: this.textCsvColumnSize,
			atb: this.textCsvColumnATB,
			atc: this.textCsvColumnATC,
			mrn: this.textCsvColumnMRN,
			entering_port_date: this.textCsvColumnEnteringPortDate,
			terminal: this.textCsvColumnTerminal,
			ship: this.textCsvColumnShip,
			shipowner: this.textCsvColumnShipowner,
			empty_return_address: this.textCsvColumnEmptyReturnAddress,
			loading_port: this.textCsvColumnLoadingPort,
			logistic_status: this.textCsvColumnLogisticStatus,
			financial_status: this.textCsvColumnFinancialStatus,
			customs_status: this.textCsvColumnCustomsStatus,
		}

		const selectedIds = this.selectedCheckboxes.map(checkbox => checkbox.id)

		const data = this.model.items
			.filter(item => selectedIds.includes(item.id))
			.map(item => ({
				container_number: item.container_number,
				number: item.number,
				conveyance_type: item.conveyance_type ? item.conveyance_type.name : '',
				take_date: item.take_date ? this.formatDate(item.take_date) : '',
				delivery_address: this.formatDeliveryAddress(item),
				status: item.status?.name,
				taken_by:
					(item.taken_by?.first_name ? item.taken_by?.first_name : '') +
					(item.taken_by?.first_name && item.taken_by?.last_name ? ' ' : '') +
					(item.taken_by?.last_name ? item.taken_by?.last_name : ''),
				bl_number: item.container_bl || '',
				weight: item.container_weight || '',
				size: this.getValueDictionaryTranslation(item.container_size) || '',
				atb: item.container_atb || '',
				atc: item.container_atc || '',
				mrn: item.container_mrn || '',
				entering_port_date: item.entering_port_date || '',
				terminal: item.pickup_terminal?.name || '',
				ship: item.ship_name || '',
				shipowner: item.shipowner?.name || '',
				empty_return_address:
					(item.return_address_country ? item.return_address_country : '') +
					' ' +
					(item.return_address_city ? item.return_address_city : '') +
					' ' +
					(item.return_address_street ? item.return_address_street : '') +
					' ' +
					(item.return_address_apartment_number ? item.return_address_apartment_number : '') +
					' ' +
					(item.return_address_building_number ? item.return_address_building_number : '') +
					' ' +
					(item.return_address_postal_code ? item.return_address_postal_code : ''),
				loading_port: this.getValueDictionaryTranslation(item.loading_port) || '',
				logistic_status: item.container_logistic_status?.name || '',
				financial_status: item.order_payment_status?.name || '',
				customs_status: item.container_clearance_status?.name || '',
				order: item,
			}))
		try {
			this.csvExportService.exportToCsv(data, headersMap, `${this.textCsvFilename}.csv`)
			this.notificationService.success($localize`CSV has been generated`)
		} catch (error) {
			this.notificationService.error($localize`Application error`)
			console.error(error)
		}
	}

	formatDeliveryAddress(item: any): string {
		const country = this.getCountryName(item.delivery_address_country as CountryCode)
		const streetAndNumber = [
			item.delivery_address_street,
			item.delivery_address_building_number,
			item.delivery_address_apartment_number,
		]
			.filter(Boolean)
			.join(' ')

		const addressParts = [item.delivery_address_city, streetAndNumber, item.delivery_address_postal_code, country]
		return addressParts.filter(Boolean).join(', ')
	}

	getCountryName(countryCode: CountryCode): string | undefined {
		return getCountryNameByCode(countryCode, getLanguageCode(this.languageService.getSelectedLanguage ?? 'en'))
	}

	handleOrderDelete(order?: OrderReadListItem | OrderReadListItem[]): void {
		if (!order) {
			if (!this.selectedCheckboxes.length) {
				this.notificationService.error($localize`Please select at least one element`)
				return
			}

			order = this.selectedCheckboxes
		}

		if (!order) {
			this.notificationService.error($localize`Application error`)
			return
		}
		const ids: string | string[] = Array.isArray(order) ? order.map(e => e.id) : order!.id
		this.modalService.open(ConfirmationComponent).then(modal => {
			modal.title = $localize`Danger`
			modal.componentInstance.message =
				typeof ids === 'string' || (isArray(ids) && ids.length === 1)
					? $localize`Are you sure to remove this order?`
					: $localize`Are you sure to remove these orders?`
		})

		this.modalService.modalRef.closed.subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.deleteOrder(ids)
			}
		})
	}

	handleClearFilters() {
		this.refreshFilters = true
		this.initFilters()
		setTimeout(() => {
			this.refreshFilters = false
		})
		sessionStorage.removeItem(OrderListScrollStorageKey)
		this.pagination.reset()
		this.loaderService.setContain().enable(this.fetchData.bind(this))
	}

	areArraysEqual(firstArray: string[] | undefined, secondArray: string[] | undefined): boolean {
		if (!firstArray && !secondArray) return true
		if (!firstArray || !secondArray) return false
		if (firstArray.length !== secondArray.length) return false

		for (let i = 0; i < firstArray.length; i++) {
			if (firstArray[i] !== secondArray[i]) return false
		}

		return true
	}

	handleTableFilters(withoutDebounce = false, listCloseKey?: 'entity' | 'status' | 'assignedTo'): void {
		if (!this.isColumnFilters) {
			return
		}
		if (listCloseKey) {
			switch (listCloseKey) {
				case 'entity':
					if (
						this.areArraysEqual(
							this.filtersValue.entity?.map(e => e.id),
							this.filters.value.entity?.map(e => e.id),
						)
					) {
						return
					}
					break
				case 'status':
					if (
						this.areArraysEqual(
							this.filtersValue.status?.map(e => e.id),
							this.filters.value.status?.map(e => e.id),
						)
					) {
						return
					}
					break
				case 'assignedTo':
					if (
						this.areArraysEqual(
							this.filtersValue.takenBy?.map(e => e.id),
							this.filters.value.takenBy?.map(e => e.id),
						)
					) {
						return
					}
					break
			}
		}
		if (this.tableFiltersTimeout) {
			clearTimeout(this.tableFiltersTimeout)
		}
		const action = () => {
			this.pagination.reset()
			this.pagination.reloadList()
		}
		if (withoutDebounce) {
			action()
		}
		this.tableFiltersTimeout = setTimeout(() => {
			action()
		}, 200)
	}

	handleFilters() {
		if (this.filters.invalid) {
			this.notificationService.error($localize`Form contains errors`)
			return
		}
		this.pagination.reset()
		this.loaderService.setContain().enable(this.fetchData.bind(this))
		this.orderKanbanComponent?.resetKanban()
	}

	fetchUsers(searchTerm?: string) {
		this.usersService.getUsers(0, 15, searchTerm ? encodeURIComponent(searchTerm) : undefined).subscribe({
			next: response => {
				this.users = response
			},
			error: error => {
				if (error.code == 422) {
					console.error('invalid query on fetch request')
				}
				const errorMessage = $localize`Error during fetching users`
				this.notificationService.error(errorMessage)
			},
		})
	}

	fetchContractors(searchTerm?: string): void {
		this.contractorService.getContractors(0, 15, searchTerm ? encodeURIComponent(searchTerm) : undefined).subscribe({
			next: (response: ContractorReadList) => {
				this.contractors = response
			},
			error: error => {
				if (error.code == 422) {
					console.error('invalid query on fetch request')
				}
				const errorMessage = $localize`Error during fetching contractors`
				this.notificationService.error(errorMessage)
			},
		})
	}

	fetchEntities(searchTerm?: string): void {
		if (!this.aclCanReadEntities()) {
			return
		}
		if (this.contractorId) {
			this.contractorService
				.getEntities(
					0,
					15,
					searchTerm ? encodeURIComponent(searchTerm) : undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					this.contractorId,
				)
				.subscribe({
					next: (response: EntityReadList) => {
						this.entities = response
					},
					error: error => {
						if (error.code == 422) {
							console.error('invalid query on fetch request')
						}
						const errorMessage = $localize`Error during fetching contractors`
						this.notificationService.error(errorMessage)
					},
				})
		} else {
			this.contractorService.getEntities(0, 15, searchTerm ? encodeURIComponent(searchTerm) : undefined).subscribe({
				next: (response: EntityReadList) => {
					this.entities = response
				},
				error: error => {
					if (error.code == 422) {
						console.error('invalid query on fetch request')
					}
					const errorMessage = $localize`Error during fetching contractors`
					this.notificationService.error(errorMessage)
				},
			})
		}
	}

	scrollToOrders() {
		setTimeout(() => {
			const element = document.querySelector('.orders')
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' })
			}
		}, 0)
	}

	toggleView() {
		this.loaderService.setContain().enable(async () => {
			this.showKanban = !this.showKanban
			this.setLocalStorageViewType(this.showKanban ? 'kanban' : 'list')
			this.fetchStatuses(this.showKanban)
			this.pagination.reset()
			this.loaderService.setContain().enable(this.fetchData.bind(this))
			// this.handleClearFilters()
		})
	}

	async changeOrderStatus(order?: OrderReadListItem | OrderReadListItem[]) {
		let orders: OrderRead[] = []
		if (!order) {
			if (!this.selectedCheckboxes.length) {
				this.notificationService.error($localize`Please select at least one element`)
				return
			}
			orders = this.selectedCheckboxes
		}

		if (!order && !orders) {
			this.notificationService.error($localize`Application error`)
			return
		}

		const ids: string[] = orders.map(e => e.id)

		const modal = await this.modalService.open(ChangeStatusComponent)
		modal.title = order || ids.length == 1 ? $localize`Change status of order` : $localize`Change status of orders`
		modal.iconName = 'show-all'
		modal.width = '400px'
		modal.componentInstance.order = order ? order : undefined
		modal.componentInstance.ids = ids ? ids : undefined
		this.modalService.modalRef.closed.subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.loaderService.setContain().enable(this.fetchData.bind(this))
			}
		})
	}

	async handleContractorChange(order: OrderReadListItem, index: number) {
		if (order.entity) {
			const modal: ModalComponent<EntityChangeComponent> = await this.modalService.open(EntityChangeComponent)
			modal.title = $localize`Change contractor`
			modal.iconName = 'users'
			modal.componentInstance.orderId = order.id
			modal.componentInstance.entityId = order.entity.id
			modal.componentInstance.orderNumber = order.number ?? ''
			modal.afterClosed.subscribe((order?: OrderRead) => {
				if (order) {
					this.model.items[index] = order
				}
			})
		}
	}

	async changeAssignationOrder(order?: OrderReadListItem | OrderReadListItem[]) {
		let orders: OrderRead[] = []
		if (!order) {
			if (!this.selectedCheckboxes.length) {
				this.notificationService.error($localize`Please select at least one element`)
				return
			}
			orders = this.selectedCheckboxes
		}

		if (!order && !orders) {
			this.notificationService.error($localize`Application error`)
			return
		}

		const ids: string[] = orders.map(e => e.id)

		const modal = await this.modalService.open(ChangeAssignationComponent)
		modal.title =
			order || ids.length == 1
				? $localize`Change user assignation to order`
				: $localize`Change user assignation to orders`
		modal.iconName = 'show-all'
		modal.width = '400px'
		modal.componentInstance.order = order ? order : undefined
		modal.componentInstance.ids = ids ? ids : undefined
		this.modalService.modalRef.closed.subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.loaderService.setContain().enable(this.fetchData.bind(this))
			}
		})
	}

	openInNewWindow(order: OrderReadListItem): void {
		window.open(`/orders/${order.id}`, '_blank')
	}

	async changeConveyanceType(order?: OrderReadListItem) {
		const modal = await this.modalService.open(ChangeConveyanceTypeComponent)
		modal.title = $localize`Change conveyance type`
		modal.iconName = 'show-all'
		modal.width = '400px'
		modal.componentInstance.order = order
		this.modalService.modalRef.closed.subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.loaderService.setContain().enable(this.fetchData.bind(this))
			}
		})
	}

	formatDate(date: string): string {
		const d = new Date(date)
		const year = d.getFullYear()
		const month = ('0' + (d.getMonth() + 1)).slice(-2)
		const day = ('0' + d.getDate()).slice(-2)
		const hour = ('0' + d.getHours()).slice(-2)
		const minute = ('0' + d.getMinutes()).slice(-2)
		const second = ('0' + d.getSeconds()).slice(-2)
		return `${year}-${month}-${day} ${hour}:${minute}:${second}`
	}

	refreshList() {
		// this.loaderService.setContain().enable(this.fetchData.bind(this))
	}

	navigateToDetails(order: OrderReadListItem): void {
		this.setLocalStorageScroll(order.id)
		this.loaderService.setContain().enable(() => {
			this.router.navigateByUrl(`/orders/${order.id}`)
		})
	}

	aclCanCreateOrder(): boolean {
		const permissions = [Permission.ORDERS_CREATE]
		return this.aclService.hasPermission(permissions)
	}

	aclCanUpdateOrders(): boolean {
		const permissions = [Permission.ORDERS_UPDATE]
		return this.aclService.hasPermission(permissions)
	}

	aclCanDeleteOrder(): boolean {
		const permissions = [Permission.ORDERS_DELETE]
		return this.aclService.hasPermission(permissions)
	}

	aclCanReadConveyanceTypes(): boolean {
		const permissions = [Permission.CONVEYANCE_TYPES_READ]
		return this.aclService.hasPermission(permissions)
	}

	aclCanReadUsers(): boolean {
		const permissions = [Permission.USERS_READ]
		return this.aclService.hasPermission(permissions)
	}

	aclCanReadContractors(): boolean {
		const permissions = [Permission.CONTRACTORS_READ]
		return this.aclService.hasPermission(permissions)
	}

	aclCanReadEntities(): boolean {
		const permissions = [Permission.ENTITIES_READ]
		return this.aclService.hasPermission(permissions)
	}

	aclCanReadStatuses(): boolean {
		const permissions = [Permission.STATUSES_READ]
		return this.aclService.hasPermission(permissions)
	}

	aclCanReadInvoices(): boolean {
		const permissions = [Permission.INVOICES_READ]
		return this.aclService.hasPermission(permissions)
	}

	aclCanReadCostInvoices(): boolean {
		const permissions = [Permission.COST_INVOICES_READ]
		return this.aclService.hasPermission(permissions)
	}

	async changePaymentStatus(order?: OrderReadListItem) {
		const modal = await this.modalService.open(OrderStatusUpdateComponent)
		modal.title = $localize`Change payment status`
		modal.iconName = 'show-all'
		modal.width = '400px'
		modal.componentInstance.order = order
		modal.componentInstance.category = 'order-payment'
		this.modalService.modalRef.closed.subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.loaderService.setContain().enable(this.fetchData.bind(this))
			}
		})
	}

	async changeClearanceStatus(order?: OrderReadListItem) {
		const modal = await this.modalService.open(OrderStatusUpdateComponent)
		modal.title = $localize`Change container clearance status`
		modal.iconName = 'show-all'
		modal.width = '400px'
		modal.componentInstance.order = order
		modal.componentInstance.category = 'container-clearance'
		this.modalService.modalRef.closed.subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.loaderService.setContain().enable(this.fetchData.bind(this))
			}
		})
	}

	async changeLogisticStatus(order?: OrderReadListItem) {
		const modal = await this.modalService.open(OrderStatusUpdateComponent)
		modal.title = $localize`Change container logistic status`
		modal.iconName = 'show-all'
		modal.width = '400px'
		modal.componentInstance.order = order
		modal.componentInstance.category = 'container-logistic'
		this.modalService.modalRef.closed.subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.loaderService.setContain().enable(this.fetchData.bind(this))
			}
		})
	}

	goToContractor(contractor: ContractorRead) {
		this.loaderService.setContain().enable(() => {
			this.router.navigateByUrl(`/contractors/${contractor.id}`)
		})
	}

	getValueDictionaryTranslation(dictionary?: string | ValueDictionaryRead | null): string {
		if (!dictionary) {
			return ''
		}

		if (typeof dictionary !== 'string') {
			return this.getItemTranslation(dictionary)
		} else if (typeof dictionary === 'string') {
			const obj = JSON.parse(dictionary)
			if (!obj.en) {
				return ''
			}
			const defaultValue = obj.en
			const selectedLanguage = this.languageService.getSelectedLanguage?.substring(0, 2) ?? 'en'

			return selectedLanguage && obj[selectedLanguage.substring(0, 2)]
				? obj[selectedLanguage.substring(0, 2)]
				: defaultValue
		}

		return ''
	}

	setLocalStorageViewType(type: OrdersViewType): void {
		sessionStorage.setItem(OrderViewLocalStorageKey, type)
	}

	setLocalStorageFiltersAndSort(): void {
		const filters = this.getLocalStorageFilterAndSort
		if (!this.isOrdersPage) {
			return
		}
		sessionStorage.setItem(
			OrderFiltersLocalStorageKey,
			JSON.stringify({
				filters: this.filters.value,
				sortOrder: this.params.sortOrder,
				sortBy: this.params.sortBy,
				perPage: this.pagination.perPage,
				pageNumber: filters?.pageNumber ?? 1,
			}),
		)
	}

	setLocalStorageScroll(target: string) {
		if (!this.isOrdersPage) {
			return
		}

		sessionStorage.setItem(
			OrderListScrollStorageKey,
			JSON.stringify({
				scrollY: this.scrollY,
				scrollTargetId: target,
			}),
		)
	}

	setLocalStoragePageNumber(): void {
		if (!this.isOrdersPage) {
			return
		}
		sessionStorage.setItem(
			OrderFiltersLocalStorageKey,
			JSON.stringify({
				filters: this.filters.value,
				sortOrder: this.params.sortOrder,
				sortBy: this.params.sortBy,
				perPage: this.pagination.perPage,
				pageNumber: this.pagination.currentPage,
			}),
		)
	}

	isOptionSelected(status: StatusItem): boolean {
		if (status.status) {
			return !!this.filters.controls.status.value?.includes(status.status)
		}
		return false
	}

	showEntityName(item: EntityReadShort | null | undefined) {
		let entityName = ''
		if (item) {
			if (item.short_name) {
				entityName = item.short_name
			} else if (item.name) {
				let name: string = (item.name ?? '').slice(0, 16)
				if (item.name.length > 10) {
					name += '...'
				}
				entityName = name
			}
		}
		return entityName
	}

	showComment(item: string | null | undefined) {
		return item
	}

	checkDateAndDsk(item: OrderReadListItem) {
		if (item.status?.is_finished) {
			return false
		}

		// if (item.container_dsk && item.container_dsk.length > 0) {
		// 	return true
		// }

		if (item.entering_port_date) {
			const today = new Date()
			const enteringPortDate = new Date(item.entering_port_date)

			if (enteringPortDate <= today) {
				return true
			}
		}
		return false
	}

	async openDeliveryConfirmationModal(order: OrderReadListItem): Promise<void> {
		if (!this.aclCanUpdateOrders()) {
			return
		}
		const modal = await this.modalService.open<OrderDeliveryComponent>(OrderDeliveryComponent)
		modal.title = this.textHasDeliveryConfirmation
		modal.componentInstance.form.patchValue(order.has_delivery_confirmation ? 'selected' : 'unselected')

		modal.componentInstance.onSubmit.subscribe({
			next: (data: 'selected' | 'unselected') => {
				this.ordersService
					.patchOrder(order.id, {
						has_delivery_confirmation: data === 'selected',
					})
					.subscribe({
						next: response => {
							order = response
							this.notificationService.success($localize`Delivery address has been changed`)
							modal.close()
						},
						error: () => {
							this.notificationService.error($localize`Failed to change delivery`)
						},
					})
			},
		})
	}

	fetchTerminal(search?: string): void {
		this.ordersService.getPickupTerminals(0, 10, search).subscribe({
			next: response => {
				this.pickupTerminals = response.items
			},
			error: () => {
				this.notificationService.error($localize`Failed to fetch pickup terminals`)
			},
		})
	}

	changeFiltersStyle(): void {
		this.isColumnFilters = !this.isColumnFilters
		sessionStorage.setItem(
			OrderFiltersStyleLocalStorageKey,
			this.isColumnFilters ? OrderFiltersStyleEnum.column : OrderFiltersStyleEnum.default,
		)
	}

	private deleteOrder(ids: string | string[]) {
		if (typeof ids === 'string') {
			ids = [ids]
		}
		const messages: { id: string; success: boolean }[] = []
		const lastMessage = () => {
			if (messages.filter(message => message.success).length) {
				this.notificationService.success($localize`Orders successfully deleted`)
				this.loaderService.setContain().enable(this.fetchData.bind(this))
			} else {
				this.notificationService.error($localize`Was not able to delete orders`)
				this.loaderService.disable()
			}
		}

		for (const orderId of ids) {
			this.ordersService.deleteOrder(orderId).subscribe({
				next: () => {
					messages.push({ id: orderId, success: true })
					if (messages.length === ids.length) {
						lastMessage()
					}
				},
				error: () => {
					messages.push({ id: orderId, success: false })
					if (messages.length === ids.length) {
						lastMessage()
					}
				},
			})
		}
	}

	private fetchStatuses(switchToKanban?: boolean) {
		forkJoin({
			order_status: this.ordersService.getStatuses('order', true),
			logistic_status: this.ordersService.getStatuses('container-logistic'),
			payment_status: this.ordersService.getStatuses('order-payment'),
			customs_status: this.ordersService.getStatuses('container-clearance'),
		})
			.pipe(finalize(() => {}))
			.subscribe({
				next: responses => {
					this.filters.controls.status.reset()
					this.statusesFilters = []
					this.statuses = []
					const groupedStatuses: { [key: string]: StatusRead[] } = {}
					const filters = this.getLocalStorageFilterAndSort

					const newFilters = [
						...(!switchToKanban
							? responses.order_status.filter(item =>
									filters?.filters.status?.map(assigned => assigned.id).includes(item.id),
								)
							: []),
						...responses.logistic_status.filter(item =>
							filters?.filters.status?.map(assigned => assigned.id).includes(item.id),
						),
						...responses.payment_status.filter(item =>
							filters?.filters.status?.map(assigned => assigned.id).includes(item.id),
						),
						...responses.customs_status.filter(item =>
							filters?.filters.status?.map(assigned => assigned.id).includes(item.id),
						),
					]

					this.filters.controls.status.patchValue(newFilters)
					if (!switchToKanban) {
						for (const status of responses.order_status) {
							const category: string = this.textStatus
							if (groupedStatuses[category]) {
								groupedStatuses[category].push(status)
							} else {
								groupedStatuses[category] = [status]
							}
						}
					}
					for (const status of responses.payment_status) {
						const category: string = this.textOrderPaymentStatus
						if (groupedStatuses[category]) {
							groupedStatuses[category].push(status)
						} else {
							groupedStatuses[category] = [status]
						}
					}

					for (const status of responses.logistic_status) {
						const category: string = this.textContainerLogisticStatus
						if (groupedStatuses[category]) {
							groupedStatuses[category].push(status)
						} else {
							groupedStatuses[category] = [status]
						}
					}

					for (const status of responses.customs_status) {
						const category: string = this.textContainerCustomsStatus
						if (groupedStatuses[category]) {
							groupedStatuses[category].push(status)
						} else {
							groupedStatuses[category] = [status]
						}
					}

					for (const status in groupedStatuses) {
						const statuses = groupedStatuses[status].map(t => ({ status: t }))
						this.statusesFilters.push({ dividerText: status })
						this.statusesFilters = this.statusesFilters.concat(statuses)
					}
				},
				error: () => {
					this.notificationService.error($localize`Was not able to fetch statuses`)
				},
			})
		this.ordersService.getStatuses('order').subscribe({
			next: (response: StatusRead[]) => {
				this.statuses = response.sort((a, b) => {
					const posA = a.position ?? Number.MAX_SAFE_INTEGER
					const posB = b.position ?? Number.MAX_SAFE_INTEGER

					if (posA !== posB) {
						return posA - posB
					}

					const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
					const dateB = b.created_at ? new Date(b.created_at).getTime() : 0

					return dateA - dateB
				})
				if (switchToKanban) {
					this.showKanban = true
				}
			},
			error: () => {
				const message = $localize`Was not able to fetch statuses`
				this.notificationService.error(message)
			},
		})
	}

	private fetchConveyanceTypes() {
		this.ordersService.getConveyanceTypes().subscribe({
			next: (response: ConveyanceTypeRead[]) => {
				this.conveyanceTypes = response
			},
			error: () => {
				const message = $localize`Was not able to fetch statuses`
				this.notificationService.error(message)
			},
		})
	}

	private initFilters(): void {
		this.filters = new FormGroup<OrdersFiltersInterface>(
			{
				search: new FormControl(''),
				takeDateFrom: new FormControl(null),
				takeDateTo: new FormControl(null),
				conveyanceType: new FormControl(null),
				takenBy: new FormControl(null),
				entity: new FormControl(null),
				status: new FormControl(null),
				contractor: new FormControl(null),
				delivery_confirmation: new FormControl(null),
				orderNumber: new FormControl(null),
				enteringPortDate: new FormControl(null),
				comments: new FormControl(null),
				terminal: new FormControl(null),
				blNumber: new FormControl(null),
				containerNumber: new FormControl(null),
				enteringPortDateFrom: new FormControl(null),
				enteringPortDateTo: new FormControl(null),
				without_customs_task: new FormControl(null),
			},
			{
				validators: [
					dateRangeValidator('takeDateFrom', 'takeDateTo'),
					dateRangeValidator('enteringPortDateFrom', 'enteringPortDateTo'),
				],
			},
		)
	}

	private generateAdminCSV() {
		const headersMap = {
			id: 'ID',
			created_at: this.textCsvColumnCreatedAt,
			updated_at: this.textCsvColumnUpdatedAt,
			number: this.textCsvColumnOrderNumber,
			conveyance_type: this.textCsvColumnConveyanceType,
			order_payment_status: this.textCsvColumnOrderPaymentStatus,
			container_number: this.textCsvColumnContainerNumber,
			container_logistic_status: this.textCsvColumnContainerLogisticStatus,
			container_clearance_status: this.textCsvColumnContainerClearanceStatus,
			priority: this.textCsvColumnPriority,
			take_date: this.textCsvColumnTakeDate,
			entity: this.textCsvColumnEntity,
			entering_port_date: this.textCsvColumnEnteringPortDate,
			pickup_terminal: this.textCsvColumnPickupTerminal,
			container_bl: this.textCsvColumnBlNumber,
			status: this.textCsvColumnStatus,
			taken_by: this.textCsvColumnTakenBy,
			taken_by_email: this.textCsvColumnTakenByEmail,
			container_weight: this.textCsvColumnWeight,
			container_size: this.textCsvColumnSize,
			container_atb: this.textCsvColumnATB,
			container_atc: this.textCsvColumnATC,
			container_mrn: this.textCsvColumnMRN,
			container_dsk: this.textCsvColumnDSK,
			ship_name: this.textCsvColumnShipName,
			shipowner: this.textCsvColumnShipowner,
			delivery_address: this.textCsvColumnDeliveryAddress,
			return_address: this.textCsvColumnEmptyReturnAddress,
			loading_port: this.textCsvColumnLoadingPort,
			assigned_to: this.textCsvColumnAssignedTo,
			assigned_to_email: this.textCsvColumnAssignedToEmail,
			has_delivery_confirmation: this.textCsvColumnHasDeliveryConfirmation,
			comment: this.textCsvColumnComment,
			package_count: this.textCsvColumnPackageCount,
		}

		const selectedIds = this.selectedCheckboxes.map(checkbox => checkbox.id)

		const data = this.model.items
			.filter(item => selectedIds.includes(item.id))
			.map(item => ({
				id: item.id,
				created_at: item.created_at,
				updated_at: item.updated_at,
				number: item.number || '',
				conveyance_type: item.conveyance_type?.name || '',
				order_payment_status: item.order_payment_status?.name || '',
				container_number: item.container_number || '',
				container_logistic_status: item.container_logistic_status?.name || '',
				container_clearance_status: item.container_clearance_status?.name || '',
				priority: item.priority?.name || '',
				take_date: item.take_date ? this.formatDate(item.take_date) : '',
				entity: item.entity?.name || '',
				entering_port_date: item.entering_port_date || '',
				pickup_terminal: item.pickup_terminal?.name || '',
				container_bl: item.container_bl || '',
				status: item.status?.name || '',
				taken_by: `${item.taken_by?.first_name || ''} ${item.taken_by?.last_name || ''}`.trim(),
				taken_by_email: item.taken_by?.email || '',
				container_weight: item.container_weight || '',
				container_size: this.getValueDictionaryTranslation(item.container_size) || '',
				container_atb: item.container_atb || '',
				container_atc: item.container_atc || '',
				container_mrn: item.container_mrn || '',
				container_dsk: item.container_dsk || '',
				ship_name: item.ship_name || '',
				shipowner: item.shipowner?.name || '',
				delivery_address: [
					item.delivery_address_street || '',
					item.delivery_address_building_number || '',
					item.delivery_address_apartment_number || '',
					item.delivery_address_city || '',
					item.delivery_address_postal_code || '',
					item.delivery_address_country || '',
				]
					.filter(Boolean)
					.join(' '),
				return_address: [
					item.return_address_street || '',
					item.return_address_building_number || '',
					item.return_address_apartment_number || '',
					item.return_address_city || '',
					item.return_address_postal_code || '',
					item.return_address_country || '',
				]
					.filter(Boolean)
					.join(' '),
				loading_port: this.getValueDictionaryTranslation(item.loading_port) || '',
				assigned_to: `${item.assigned_to?.first_name || ''} ${item.assigned_to?.last_name || ''}`.trim(),
				assigned_to_email: item.assigned_to?.email || '',
				has_delivery_confirmation: item.has_delivery_confirmation ? $localize`Yes` : $localize`No`,
				comment: item.comment || '',
				package_count: item.package_count || '',
			}))

		try {
			this.csvExportService.exportToCsv(data, headersMap, `${this.textCsvFilename}.csv`)
			this.notificationService.success($localize`CSV has been generated`)
		} catch (error) {
			this.notificationService.error($localize`Application error`)
			console.error(error)
		}
	}

	async copyToClipboard(item: string) {
		try {
			await navigator.clipboard.writeText(item)
			this.notificationService.success($localize`Successfully copied number to clipboard.`)
		} catch (error) {
			this.notificationService.error($localize`Something goes wrong during copying number.`)
		}
	}

	async createNewOrderFromValues(item: OrderReadListItem) {
		await this.router.navigateByUrl(
			`/${config.modules.orders.path}/create?${OrderQueryParamKeysEnum.odderID}=${item.id}`,
		)
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			for (const subscription of this.subscriptions) {
				subscription.unsubscribe()
			}
		}
	}
}
